import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import {html} from '@polymer/polymer/lib/utils/html-tag.js';
import {PAYMENT_TRANSLATIONS} from './payment-translations.js';
import {PAYMENT_CONFIG} from './payment-config.js';
import template from './ns-payment.html';
import {FormMixin} from '../formMixin.js';
import '@polymer/polymer/lib/elements/dom-repeat';
import '../ns-form/ns-form.js';
import {UserService} from '@nuskin/ns-account';

/**
 * This is the Payment Component used by the Billing Component.
 * Originally created around credit cards but should be extended to handle any type of payment to be processed.
 *
 * <section>
 *  <h2>Content Guidelines</h2>
 *  <p>Here are some guidelines on how to properly use this element.</p>
 * </section>
 *
 * <h2>Payment Component Demo</h2>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-payment></ns-payment>
 *   </template>
 * </demo-snippet>
 */
export class NsPayment extends FormMixin(PolymerElement) {
    static get is() {
        return 'ns-payment';
    }

    static get template() {
        let htmlElement = document.createElement('TEMPLATE');
        htmlElement.innerHTML = template;
        return htmlElement;
    }

    static get properties() {
        return {
            /**
             * Credit card type
             *
             * @example:visa, mastercard, american express
             */
            type: {
                type: String,
                value: 'visa'
            },
            /**
             * Country for translations
             *
             * @example:US, JP, CN
             */
            country: {
                type: String,
                observer: '_initialize'
            },
            /**
             * Language for translations
             *
             * @example: en, es, fr
             */
            language: {
                type: String,
                observer: '_initialize'
            },
            /**
             * Cardholder name
             *
             * @example: Bob Johnson
             */
            cardholderName: {
                type: String,
                notify: true
            },
            /**
             * Cardholder name input disabled
             */
            nameInputDisabled: {
                type: Boolean,
                value: false
            },
            /**
             * Card number
             *
             * @example: 1234567890123456
             */
            ccNumber: {
                type: String,
                notify: true
            },
            /**
             * Card expiration month
             *
             * @example: 01, 02, 03
             */
            month: {
                type: String,
                value: '',
                reflectToAttribute: true,
                notify: true,
                observer: '_setExpirationDate'
            },
            /**
             * Card expiration year
             *
             * @example: 2001, 2002, 2003
             */
            year: {
                type: String,
                value: '',
                reflectToAttribute: true,
                notify: true,
                observer: '_setExpirationDate'
            },
            /**
             * Array for month select options
             *
             * @example: [{'key': '01', 'value': '01'}, {'key': '02', 'value': '02'}
             */
            months: {
                type: Array,
                value: [{'key': '01', 'value': '01'},
                    {'key': '02', 'value': '02'},
                    {'key': '03', 'value': '03'},
                    {'key': '04', 'value': '04'},
                    {'key': '05', 'value': '05'},
                    {'key': '06', 'value': '06'},
                    {'key': '07', 'value': '07'},
                    {'key': '08', 'value': '08'},
                    {'key': '09', 'value': '09'},
                    {'key': '10', 'value': '10'},
                    {'key': '11', 'value': '11'},
                    {'key': '12', 'value': '12'}]
            },
            /**
             * Array for year select options generated in setYears()
             * @example: 2001, 2002, 2003
             */
            _years: {
                type: Array,
                value: [{}]
            },
            /**
             * Boolean to determine if an element has received focus
             * @example: true
             */
            _hasReceivedFocus: {
                type: Boolean,
                reflectToAttribute: true,
                value: false
            },
            /**
             * Error message for unvalidated inputs
             * @example: true
             */
            _passedValidation: {
                type: Boolean,
                reflectToAttribute: true
            },
            /**
             * Form inputs - these properties are two way bound to the values of the corresponding
             * inputs in the payment form.
             */
            addressLine1: {
                type: String,
                notify: true
            },
            addressLine2: {
                type: String,
                notify: true
            },
            city: {
                type: String,
                notify: true
            },
            state: {
                type: String,
                notify: true
            },
            zip: {
                type: String,
                notify: true
            },
            county: {
                type: String,
                notify: true
            },
            /**
             * Specifies whether the credit card field is disabled or not. For PUT requests, we want
             * it to be disabled - you can't change the card number of an existing credit card.
             */
            cardNumberDisabled: {
                type: Boolean,
                notify: true,
                value: false
            },
            /**
             * Determines whether the "Save as primary credit card" checkbox will be displayed.
             */
            hidePrimaryCardCheckbox: {
                type: Boolean,
                value: false
            },
            /**
             * Determines whether the "Save Card" checkbox will be displayed
             */
            hideSaveCardCheckbox: {
                type: Boolean,
                value: false
            },
            /**
             * Determines whether or not the payment method will be saved as the primary payment method.
             * This property is two-way bound to the "Save as primary credit card" checkbox.
             */
            isPrimaryCard: {
                type: Boolean,
                notify: true,
                value: false
            },
            /**
             * Payment types allowed based on market
             */
            paymentTypesAllowed: {
                type: Array
            },
            /**
             * Id of the selected payment type
             */
            paymentTypeId: {
                type: Number,
                notify: true
            },
            /**
             * Determines whether
             */
            showBillingAddress: {
                type: Boolean,
                value: false
            },
            /**
             * Allowed country codes for SEPA IBAN
             */
            bankCountryOptions: {
                type: Array,
                value: ["AT", "BE", "DE", "ES", "FR", "IT", "LU", "NL", "PT", "CH"],
                observer: '_onSetBankCountryOptions'
            },
            bankCountry: {
                type: String,
                notify: true
            },
            /**
             * Shows a dropdown list of countries for IBAN form
             */
            showBankCountry: {
                type: Boolean,
                value: false
            },
            sepaMandateType: {
                type: String,
                value: "paper",
                notify: true
            },
            /**
             * This is a unix timestamp. If this property is specified, the form will validate the
             * credit card expiration date based on this property rather than the current date.
             */
            earliestAllowedExpDate: {
                type: Number
            },
            dirty: {
                type: Boolean,
                value: false,
                notify: true,
                reflectToAttribute: true
            },
            /**
             * Payment type i.e. CEDN, VISA, MASTER, WIRE etc.
             */
            paymentType: {
                type: String
            },

            showPaymentStoredCredentialsAgreement: {
                type: Boolean,
                value: false
            },

            /**
             * Encoded value of the close icon used for passing this value to the template
             */
            closeIcon: {
                type: String,
            },
            serverTranslations:{
                type: Object,
                value: function() {
                    return {}
                }
            }
        };
    }

    static get observers() {
        return [
            '_updateDirtyFlag(cardholderNameDirty, ccNumberDirty, ccMonthDirty, ccYearDirty, ccAddressDirty)',
            '_configurePaymentForm(paymentType, cardNumberDisabled)',
            '_onAgreementChecked(ddAgreementChecked)',
            '_onUpdateSepaMandate(showSepaMandateOptions, ddAgreementChecked)'
        ];
    }

    /**
     * Sets translations, regex rules, and element attributes
     */
    ready() {
        super.ready();
        this._initialize();
    }

    _initialize() {
        if (this.language && this.country && this.language.length === 2 && this.country.length === 2) {
            this._localize(this.language, this.country, PAYMENT_TRANSLATIONS, this.serverTranslations, true);
            this._setConfig(this.country, PAYMENT_CONFIG);
            this._setYears();
            if (this.paymentType) this._configurePaymentForm(this.paymentType, this.cardNumberDisabled);
        }
    }

    /**
     * Validate method for payment form
     */
    validateExpirationDate() {
        if (!this.hideExpiration) {
            const earliestAllowedExpDate = this.earliestAllowedExpDate ? new Date(this.earliestAllowedExpDate) : new Date();
            let thisMonth = earliestAllowedExpDate.getMonth() + 1;
            let thisYear = earliestAllowedExpDate.getFullYear();
            let selectedMonth = parseInt(this.month);
            let selectedYear = parseInt(this.year);

            if (this.shadowRoot.querySelector('#month') && this.shadowRoot.querySelector('#year')) {
                this.shadowRoot.querySelector('#month').requiredMessage = this.monthError;
                this.shadowRoot.querySelector('#year').requiredMessage = this.yearError;
                if (selectedYear < thisYear) {
                    this.yearError = this.expiredErrorMessage;
                    this.shadowRoot.querySelector('#year').invalid = true;
                    return false;
                } else if ((selectedYear === thisYear) && (selectedMonth < thisMonth)) {
                    this.monthError = this.expiredErrorMessage;
                    this.shadowRoot.querySelector('#month').invalid = true;
                    return false;
                } else {
                    this.shadowRoot.querySelector('#month').invalid = false;
                    this.shadowRoot.querySelector('#year').invalid = false;
                    return true;
                }
            }
        }
        return true;
    }

    validate() {
        this.valid = this.validateExpirationDate();
        if (this.showDirectDebitAgreement && !this.ddAgreementChecked) {
            this.valid = false;
            this.ddAgreementClass = "sepa-agreement-error";
        }
        return this.valid && this.shadowRoot.querySelector('ns-form').validate();
    }

    /**
     * Generates an array of years to be used by the years select element
     */
    _setYears() {
        const NUM_YEARS_EXP = 10;
        const earliestAllowedExpDate = this.earliestAllowedExpDate ? new Date(this.earliestAllowedExpDate) : new Date();
        let today = earliestAllowedExpDate.getFullYear();

        let years = [];
        for (let i = today; i < today + NUM_YEARS_EXP; i++) {
            years.push({'key': i, 'value': i});
        }
        this.years = years;
    }

    /**
     * Observer for expiration month and year
     */
    _setExpirationDate () {
        this._configurePaymentForm(this.paymentType, this.cardNumberDisabled);
        if (this.hideExpiration) {
            this.month = "";
            this.year = "";
        } else {
            if (this.month === null || this.month === "" ) this.month = this._setDefaultMonth();
            if (this.year === null || this.year === "") this.year = this._setDefaultYear();
        }
        this.validateExpirationDate();
    }

    /**
     * Sets the default month if no expiration month is set.  Usually used for new form
     */
    _setDefaultMonth () {
        let month;
        if (this.earliestAllowedExpDate) {
          month = new Date(this.earliestAllowedExpDate).getMonth() + 1;
        } else {
          month = new Date().getMonth() + 1;
        }
        return (month <= 9) ? `0${month}` : month;
      }

      /**
     * Sets the default year if no expiration year is set.  Usually used for new form
     */
      _setDefaultYear() {
        let fullYear;
        if (this.earliestAllowedExpDate) {
            fullYear = new Date(this.earliestAllowedExpDate).getFullYear();
        } else {
            fullYear = new Date().getFullYear();
        }
        return fullYear;
      }

    /**
     * onchange for the "Save as primary credit card" checkbox. Toggles the value of
     * this.isPrimaryCard
     */
    _primaryCardCheckboxOnChange() {
        this.isPrimaryCard = !this.isPrimaryCard;
    }

    _configurePaymentForm(paymentType, cardNumberDisabled) {
        this.set('nameInputDisabled', false);

        // Default Form Configuration
        let hideCardholderName = false,
            cardholderNameLabel = this.paymentCardholderNameLabel,
            hideCardNumber = false,
            hideExpiration = false,
            showBankCountry = false,
            validationDisabled = cardNumberDisabled,
            showDirectDebitAgreement = false;

        // Special Form Configurations
        switch(paymentType) {
            case 'CEDN':
                hideCardholderName = true;
                hideExpiration = true;
                break;
            case 'SDD':
                cardholderNameLabel = this.ibanAccountHolderNameLabel;
                hideExpiration = true;
                showBankCountry = true;
                // Direct Debit agreement section is disabled until ADR Manage app supports authorization redirects for SEPA
                // showDirectDebitAgreement = true;
                validationDisabled = true;

                let user = UserService.getUser();
                if (user) {
                    // I'm not sure why but the initialization of ns-input clears then so I have to wait to set it
                    setTimeout(() => {
                        this.set('cardholderName', user.localName);
                        this.set('nameInputDisabled', true);
                    });
                }
                break;
        }

        this.set('hideCardholderName', hideCardholderName);
        this.set('_cardholderNameLabel', cardholderNameLabel);
        this.set('hideCardNumber', hideCardNumber);
        this.set('hideExpiration', hideExpiration);
        this.set('showBankCountry', showBankCountry);
        this.set('validationDisabled', validationDisabled);
        this.set('showDirectDebitAgreement', showDirectDebitAgreement);
    }

    /**
     *
     * @param {string[]} options
     */
    _onSetBankCountryOptions(options) {
        if (options.length > 0 && !this.cardNumberDisabled && this.showBankCountry && !this.bankCountry) {
            if (options.includes(this.country)) {
                this.set('bankCountry', this.country);
            } else {
                this.set('bankCountry', options[0]);
            }
        }
    }

    _onAgreementChecked(checked) {
        this.ddAgreementClass = checked ? "" : "sepa-agreement-error";
    }

    _onUpdateSepaMandate(showSepaMandateOptions, ddAgreementChecked) {
        if (!showSepaMandateOptions) {
            this.set('showSepaRadioGroup', false);
        } else if (ddAgreementChecked) {
            this.set('showSepaRadioGroup', true);
        }
    }

    _closeTermsAndConditionsDialog() {
        this.$.termsAndConditionsDialog.close();
    }

    _openTermsAndConditionsDialog() {
        this.$.termsAndConditionsDialog.open();
    }
}

customElements.define(NsPayment.is, NsPayment);
